/**
 * The Tablero's browser view in the URL (issue #336): verdict (KPI card),
 * motivo, search and page, so reload, share and back-from-detail restore the
 * whole view — `?decision=REJECTED&reason=CURP_MISMATCH&q=perez&page=2`.
 *
 * Pure parse/serialize. Every param is validated on its own and an invalid one
 * falls back to its default without touching the others; defaults are omitted
 * so the plain Tablero URL stays clean.
 */

import type { DecisionFilter, ReasonFilter } from "./query";

export type TableroView = {
  decision: DecisionFilter;
  reason: ReasonFilter;
  /** The search as typed (the 2-character rule is applied when filtering). */
  q: string;
  page: number;
};

export const DEFAULT_VIEW: TableroView = { decision: "ALL", reason: "ALL", q: "", page: 1 };

/** The verdicts a KPI card can select (AWAITING_RESUBMISSION has no card). */
const DECISIONS: readonly string[] = ["APPROVED", "REJECTED", "HUMAN_REVIEW"];

function parsePage(raw: string | null): number {
  if (raw === null || !/^[1-9]\d{0,5}$/.test(raw)) return 1;
  return Number(raw);
}

/** URL → view; `reasonCodes` is the motivo filter's closed option list. */
export function parseTableroView(
  params: URLSearchParams,
  reasonCodes: readonly string[],
): TableroView {
  const decision = params.get("decision");
  const reason = params.get("reason");
  return {
    decision:
      decision !== null && DECISIONS.includes(decision)
        ? (decision as DecisionFilter)
        : "ALL",
    reason: reason !== null && reasonCodes.includes(reason) ? reason : "ALL",
    q: params.get("q") ?? "",
    page: parsePage(params.get("page")),
  };
}

/** View → URL params, on top of `base` (unrelated params are kept). */
export function serializeTableroView(
  view: TableroView,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const params = new URLSearchParams(base);
  const set = (key: string, value: string | null) =>
    value === null ? params.delete(key) : params.set(key, value);
  set("decision", view.decision === "ALL" ? null : view.decision);
  set("reason", view.reason === "ALL" ? null : view.reason);
  set("q", view.q.trim() ? view.q : null);
  set("page", view.page > 1 ? String(view.page) : null);
  return params;
}

/** Apply a change; a new verdict, motivo or search starts over at page 1 so a
 * filter change never strands the reviewer on an out-of-range page. */
export function applyViewChange(
  view: TableroView,
  patch: Partial<TableroView>,
): TableroView {
  const next = { ...view, ...patch };
  const refiltered =
    next.decision !== view.decision || next.reason !== view.reason || next.q !== view.q;
  if (refiltered && patch.page === undefined) next.page = 1;
  return next;
}
